'use client'

import { DELIVERY_METHODS, type DeliveryMethod } from '@repo/core/delivery'
import { Package, Store } from 'lucide-react'

import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * How the gear gets to the customer (spec §11 · I6).
 *
 * > Wybór w koszyku: odbiór osobisty (Wrocław 9 do 18) albo kurier InPost.
 *
 * Two things ride on this and the copy says both, because neither is obvious
 * and both cost money: the courier holds the shelf for two days either side
 * instead of one, and the hire's clock starts from InPost's "Dostarczone"
 * rather than from the moment somebody walks out of the office. The second is
 * in the customer's favour and worth saying out loud.
 *
 * Shown only when the basket actually holds equipment. A basket of thumbnails
 * being asked how it would like its camera delivered is a question about
 * nothing.
 */
const OPTIONS: Record<DeliveryMethod, { icon: typeof Store; title: string; body: string }> = {
  PICKUP: {
    icon: Store,
    title: 'Odbiór osobisty',
    body: 'Wrocław, 9:00–18:00. Najem liczymy od godziny odbioru.',
  },
  COURIER: {
    icon: Package,
    title: 'Kurier InPost',
    body: 'Wysyłka i zwrot. Najem liczymy od statusu „Dostarczone" — nie od nadania.',
  },
}

export function DeliveryChoice({
  value,
  onChange,
}: {
  readonly value: DeliveryMethod
  readonly onChange: (next: DeliveryMethod) => void
}) {
  return (
    <fieldset className="mt-4">
      <legend className={cn(META, 'mb-2 font-semibold text-white')}>Sprzęt — jak odbierasz?</legend>
      <div className="flex flex-col gap-2">
        {DELIVERY_METHODS.map((method) => {
          const { icon: Icon, title, body } = OPTIONS[method]
          const active = value === method
          return (
            <label
              key={method}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors',
                active
                  ? 'border-white/35 bg-white/[0.07]'
                  : 'border-white/[0.12] bg-white/[0.03] hover:border-white/25',
              )}
            >
              <input
                type="radio"
                name="dostawa"
                value={method}
                checked={active}
                onChange={() => onChange(method)}
                className="mt-0.5 size-4 shrink-0 accent-white"
              />
              <span className="min-w-0">
                <span className={cn(META, 'flex items-center gap-1.5 font-bold text-white')}>
                  <Icon size={13} aria-hidden="true" />
                  {title}
                </span>
                <span className={cn(MICRO, 'mt-1 block leading-relaxed text-white/55')}>
                  {body}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
